import { ReportData } from '@/types/control';
import http from '../services/http';

// 终端设备健康检测响应类型
interface HealthResponse {
  status: 'healthy' | 'unhealthy';
  timestamp: number;
  services: {
    websocket: boolean;
    ipc: boolean;
    ai_process: boolean;
  };
}

// AI会记状态响应类型
interface AIStatusResponse {
  isRecording: boolean;
  streamName?: string;
}

interface ControlConnectionData {
  wsUrl: string;
  status: 'ready' | 'busy';
  connectionId?: string;
}

// 启动录制数据类型
interface StartRecordingData {
  streamName: string;
  startTime?: number;
}

// 设备名称响应类型
interface DeviceNameResponse {
  deviceName: string;
}

// 设备SN响应类型
interface DeviceSnResponse {
  deviceSn: string;
}

// 通过连接码获取大板网络信息
export async function getBoardAddress(code: string) {
  try {
    const res = await http.get<{ reportData: ReportData }>(`/api/auth/live-record/link-board/address?code=${code}`);
    return res.data;
  } catch (e) {
    console.log('getBoardAddress error >>>> ', e);
    return null;
  }
}

// 终端设备健康检测
export async function checkDeviceHealth(deviceIP: string, port: string = '2998') {
  try {
    const deviceUrl = `http://${deviceIP}:${port}/api/health`;
    const response = await http.get<HealthResponse>(deviceUrl, {
      timeout: 5000, // 5秒超时
      headers: {
        'Authorization': '', // 清空Authorization头，终端设备不需要token
      }
    });
    console.log('response >>>> ', response.data);
    return {
      success: true,
      data: response.data,
      healthy: response.data?.status === 'healthy'
    };
  } catch (error: any) {
    console.error('设备健康检测失败:', error);
    let errorMessage = '健康检测失败';

    if (error.errMsg) {
      errorMessage = error.errMsg;
    } else if (error.message) {
      errorMessage = error.message;
    }

    if (errorMessage.includes('timeout')) {
      errorMessage = '连接超时，请检查网络';
    }
    if (errorMessage.includes('Network Error')) {
      errorMessage = '连接失败，请稍后再试';
    }

    return {
      success: false,
      error: errorMessage,
      healthy: false
    };
  }
}

export async function getControlConnection(deviceIP: string, port: string = '2998'): Promise<ControlConnectionData> {
  try {
    const deviceUrl = `http://${deviceIP}:${port}/api/startControl`;
    const response = await http.get<ControlConnectionData>(deviceUrl, {
      timeout: 5000, // 5秒超时
      headers: {
        'Authorization': '', // 清空Authorization头，终端设备不需要token
      }
    });
    return response.data;
  } catch (error: any) {
    console.error('获取控制连接失败:', error);
    return {
      wsUrl: '',
      status: 'ready',
      connectionId: ''
    };
  }
}

// 获取AI会记状态
export async function getAIRecordingStatus(deviceIP: string, port: string = '2998') {
  try {
    const deviceUrl = `http://${deviceIP}:${port}/api/startStatus`;
    // 使用绝对URL，axios会忽略baseURL
    const response = await http.get<AIStatusResponse>(deviceUrl, {
      timeout: 5000, // 5秒超时
      headers: {
        'Authorization': '', // 清空Authorization头，终端设备不需要token
      }
    });

    return {
      success: true,
      data: response.data
    };
  } catch (error: any) {
    console.error('AI状态检测失败:', error);
    let errorMessage = 'AI状态检测失败';

    if (error.errMsg) {
      errorMessage = error.errMsg;
    } else if (error.message) {
      errorMessage = error.message;
    }
    if (errorMessage.includes('timeout')) {
      errorMessage = '连接超时，请检查网络';
    }
    if (errorMessage.includes('Network Error')) {
      errorMessage = '连接失败，请稍后再试';
    }
    return {
      success: false,
      error: errorMessage,
      data: {
        isRecording: false,
        streamName: undefined
      }
    };
  }
}

// 启动终端设备录制
export async function startDeviceRecording(deviceIP: string, port: string = '2998') {
  try {
    const deviceUrl = `http://${deviceIP}:${port}/api/recording/start`;
    const response = await http.post<StartRecordingData>(deviceUrl, {}, {
      timeout: 10000, // 10秒超时
      headers: {
        'Authorization': '', // 清空Authorization头，终端设备不需要token
      }
    });

    console.log('启动设备录制响应:', response.data);

    return {
      success: true,
      data: response.data,
      streamName: response.data?.streamName
    };
  } catch (error: any) {
    console.error('启动设备录制失败:', error);
    let errorMessage = '启动设备录制失败';

    if (error.errMsg) {
      errorMessage = error.errMsg;
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      streamName: undefined
    };
  }
}

// 获取设备名称
export async function getDeviceName(deviceIP: string, port: string = '2998') {
  try {
    const deviceUrl = `http://${deviceIP}:${port}/api/deviceName`;
    const response = await http.get<DeviceNameResponse>(deviceUrl, {
      timeout: 30000, // 30秒超时
      headers: {
        'Authorization': '', // 清空Authorization头，终端设备不需要token
      }
    });

    return {
      success: true,
      deviceName: response.data?.deviceName || ''
    };
  } catch (error: any) {
    console.error('获取设备名称失败:', error);
    let errorMessage = '获取设备名称失败';

    if (error.errMsg) {
      errorMessage = error.errMsg;
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      deviceName: ''
    };
  }
}

// 获取设备SN
export async function getDeviceSn(deviceIP: string, port: string = '2998') {
  try {
    const deviceUrl = `http://${deviceIP}:${port}/api/deviceSn`;
    const response = await http.get<DeviceSnResponse>(deviceUrl, {
      timeout: 30000, // 30秒超时
      headers: {
        'Authorization': '', // 清空Authorization头，终端设备不需要token
      }
    });

    return {
      success: true,
      deviceSn: response.data?.deviceSn || ''
    };
  } catch (error: any) {
    console.error('获取设备SN失败:', error);
    let errorMessage = '获取设备SN失败';

    if (error.errMsg) {
      errorMessage = error.errMsg;
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      deviceSn: ''
    };
  }
}